import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Upload } from "lucide-react";
import { useParams } from "react-router-dom";

export default function StaffSubmission() {
  const { ptaId } = useParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    let fileUrl = null;

    // Upload file if provided
    if (file) {
      const fileExt = file.name.split('.').pop();
      const filePath = `${crypto.randomUUID()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('receipts')
        .upload(filePath, file);

      if (!uploadError) {
        const { data } = supabase.storage.from('receipts').getPublicUrl(filePath);
        fileUrl = data.publicUrl;
      }
    }

    const { error } = await supabase
      .from('staff_submissions')
      .insert({
        pta_id: ptaId,
        first_name: formData.get('first_name') as string,
        last_name: formData.get('last_name') as string,
        role: formData.get('role') as string,
        title: formData.get('title') as string,
        details: formData.get('details') as string,
        estimated_cost: formData.get('estimated_cost') as string,
        file_url: fileUrl,
      });

    setLoading(false);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Success", description: "Your request has been submitted!" });
      (e.target as HTMLFormElement).reset();
      setFile(null);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl shadow-card">
        <CardHeader>
          <CardTitle className="text-2xl">School Staff Request Form</CardTitle>
          <p className="text-sm text-muted-foreground">
            Submit a funding request to the PTA
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input id="first_name" name="first_name" required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input id="last_name" name="last_name" required />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Input id="role" name="role" placeholder="e.g., Teacher, Head of Year" required />
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Request Title *</Label>
              <Input id="title" name="title" placeholder="Brief title for your request" required />
            </div>

            <div className="space-y-2">
              <Label htmlFor="details">Details *</Label>
              <Textarea 
                id="details" 
                name="details" 
                placeholder="Provide detailed information about your request"
                rows={5}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimated_cost">Estimated Cost</Label>
              <Input 
                id="estimated_cost" 
                name="estimated_cost" 
                placeholder="e.g., £500-£750"
              />
            </div>

            <div className="space-y-2">
              <Label>Upload Quote (Optional)</Label>
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">
                  Drag and drop or click to upload
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  PNG, JPG, PDF up to 10MB
                </p>
                <Input
                  type="file"
                  accept=".png,.jpg,.jpeg,.pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="max-w-xs mx-auto"
                />
              </div>
              {file && (
                <p className="text-sm text-muted-foreground">
                  Selected: {file.name}
                </p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Submitting..." : "Submit Request"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
